import { useState, type ReactNode } from 'react';

export interface ProgressBarChartItem {
  name: string;
  percent: number;
  color: string;
}

export interface ProgressBarChartProps {
  title: string;
  subtitle?: string;
  items: ProgressBarChartItem[];
  isLoading?: boolean;
  headerRight?: ReactNode;
}

export function ProgressBarChart({
  title,
  subtitle,
  items,
  isLoading,
  headerRight,
}: ProgressBarChartProps) {
  const [hovered, setHovered] = useState<string | null>(null);

  if (isLoading && items.length === 0) {
    return (
      <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 rounded-2xl border backdrop-blur-sm">
        <div className="border-(--color-border) flex items-center justify-between gap-3 border-b px-6 py-4">
          <div>
            <p className="text-(--color-accent) text-sm font-bold">{title}</p>
            {subtitle !== undefined && (
              <p className="text-(--color-text-secondary) mt-0.5 text-xs">
                Carregando...
              </p>
            )}
          </div>
          {headerRight}
        </div>
        <div className="space-y-4 px-6 py-6">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="h-10 animate-pulse rounded-lg bg-white/10"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 rounded-2xl border backdrop-blur-sm">
      <div className="border-(--color-border) flex items-center justify-between gap-3 border-b px-6 py-4">
        <div>
          <p className="text-(--color-accent) text-sm font-bold">{title}</p>
          {subtitle !== undefined && (
            <p className="text-(--color-text-secondary) mt-0.5 text-xs">
              {subtitle}
            </p>
          )}
        </div>
        {headerRight}
      </div>

      <div className="space-y-4 px-6 py-6">
        {items.map((item) => {
          const pct = item.percent * 100;
          return (
            <div
              key={item.name}
              className="relative"
              onMouseEnter={() => setHovered(item.name)}
              onMouseLeave={() => setHovered(null)}
            >
              <div className="mb-1.5 flex items-center justify-between">
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-(--color-text-primary) truncate text-xs">
                    {item.name}
                  </span>
                </div>
                <span
                  className="ml-3 shrink-0 text-xs font-bold tabular-nums"
                  style={{ color: item.color }}
                >
                  {pct.toFixed(2)}%
                </span>
              </div>
              <div className="bg-white/8 relative h-2 cursor-pointer overflow-hidden rounded-full">
                <div
                  className="absolute left-0 top-0 h-full rounded-full transition-all"
                  style={{ width: `${pct}%`, backgroundColor: item.color }}
                />
              </div>
              {hovered === item.name && (
                <div className="border-(--color-border) bg-(--color-bg-secondary) pointer-events-none absolute left-0 top-full z-20 mt-2 rounded-lg border px-3 py-2 shadow-lg">
                  <p
                    className="text-xs font-semibold"
                    style={{ color: item.color }}
                  >
                    {item.name}
                  </p>
                  <p className="text-(--color-text-secondary) text-xs">
                    {pct.toFixed(2)}%
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
